import { computed } from "vue";
import { useStore } from "vuex";

import cloneDeep from "lodash/cloneDeep";
import { storeToRefs } from "pinia";
import { v4 as uuidv4 } from "uuid";

import { EditorNodeName } from "@/enums/customAttributes";
import { BracketOperator } from "@/enums/customAttributesV2";
import {
  getServiceAttributes,
  getServiceByName,
  getServiceHelper,
} from "@/helpers/common";
import { SERVICE_CUSTOM } from "@/helpers/constants";
import {
  ATTRIBUTE_PREFIX_CHAR,
  CLOSING_PARENTHESIS,
  CONTEXT_CLOSING_BRACKET,
  CONTEXT_OPENING_BRACKET,
  CUSTOM_ATTRIBUTE_ID,
  DELIMITERS,
  OPENING_PARENTHESIS,
  STRING_CHARS,
} from "@/helpers/constants/attributeBuilder";
import {
  createEditorNode,
  emptyOperatorAndParenthesesStack,
  handleClosingParenthesisToken,
  handleOperandToken,
  handleSeparatorToken,
  isNumber,
  popOperatorAndPushToOutput,
} from "@/helpers/customAttributes";
import { useAttributeBuilderStore } from "@/stores/customAttributeBuilder";

import type { EditorNode } from "@/models/TextEditor";
import type {
  CustomAttributeExpression,
  GroupIdentifier,
} from "@/models/customAttributes/customAttribute";
import type { FormulaOption } from "@/models/options";
import type { IAttribute } from "@/models/orchestration";
import type { DataServiceInfo } from "@/models/orchestration";
import type { IContextSetValueModel } from "@/models/orchestration";

export const useAttributeBuilderServices = () => {
  const { services, serviceDetailsMap } = storeToRefs(
    useAttributeBuilderStore()
  );

  const serviceAttributes = computed(() =>
    services.value.reduce<IAttribute[]>(getServiceAttributes, [])
  );

  const getService = (serviceId: string, context?: IContextSetValueModel) => {
    if (!serviceAttributes.value?.length || !serviceId) {
      return null;
    }
    return getServiceHelper(serviceId, serviceAttributes.value, context);
  };

  const getServiceName = (
    serviceId: string,
    context?: IContextSetValueModel
  ) => {
    return getService(serviceId, context)?.name || "";
  };

  const getServiceProviderDetails = (
    serviceId?: string
  ): DataServiceInfo | null | undefined => {
    return serviceDetailsMap.value[serviceId ?? ""];
  };

  return {
    services,
    serviceAttributes,
    getService,
    getServiceName,
    getServiceProviderDetails,
  };
};

export const useFormulaEditor = () => {
  const PARENTHESIS = [...CLOSING_PARENTHESIS, ...OPENING_PARENTHESIS];
  const { services } = storeToRefs(useAttributeBuilderStore());
  const { getters } = useStore();

  const serviceAttributes = computed(() =>
    services.value.reduce<IAttribute[]>(getServiceAttributes, [])
  );

  const attributeFunctions = computed<FormulaOption[]>(
    () => getters["options/attributeFunctions"]
  );

  const functionsByNameMap = computed<Record<string, FormulaOption>>(() => {
    return attributeFunctions.value.reduce(
      (definitions: Record<string, FormulaOption>, functionDefinition) => {
        definitions[functionDefinition.name] = functionDefinition;
        if (functionDefinition.aliases) {
          functionDefinition.aliases.forEach((alias) => {
            definitions[alias] = functionDefinition;
          });
        }
        return definitions;
      },
      {}
    );
  });

  const isAttribute = (token: string): boolean => {
    const preProcessedToken = token[0] === ":" ? token.slice(1) : token;
    return serviceAttributes.value.some((attr) => {
      return (
        attr.name === preProcessedToken ||
        (attr.id === preProcessedToken && attr.id !== SERVICE_CUSTOM)
      );
    });
  };

  const getAttribute = (name: string): IAttribute | undefined => {
    return (
      getServiceByName(name, serviceAttributes.value) ||
      getServiceHelper(name, serviceAttributes.value)
    );
  };

  const isFunction = (token: string): boolean =>
    token.toUpperCase() in functionsByNameMap.value;

  const testExpressions: [
    RegExp | ((token: string) => boolean),
    EditorNodeName,
  ][] = [
    [/,/, EditorNodeName.separator],
    [isNumber, EditorNodeName.number],
    [/[+\-*<>^%/]|&&|!=|<=|>=|==|\|\|/, EditorNodeName.operator],
    [/[()]/, EditorNodeName.parenthesis],
    [isAttribute, EditorNodeName.attribute],
    [isFunction, EditorNodeName.function],
  ];

  const getTokenType = (token: string): EditorNodeName => {
    const trimmedToken = token.trim();
    for (const [test, type] of testExpressions) {
      if (
        typeof test === "function"
          ? test(trimmedToken)
          : test.test(trimmedToken)
      ) {
        return type;
      }
    }

    return EditorNodeName.unrecognized;
  };

  const addAttributeId = (node: EditorNode) => {
    if (node.attributeId) {
      return node;
    }
    if (node.type === EditorNodeName.attribute) {
      const prePorcessedNodeContent =
        node.content[0] === ATTRIBUTE_PREFIX_CHAR
          ? node.content.slice(1)
          : node.content;
      const attribute = getAttribute(prePorcessedNodeContent);
      if (attribute) {
        node.attributeId = attribute.id;
        node.context = attribute.context;
        node.content = attribute.name;
      }
    }
    return node;
  };

  const tokenize = (
    infix: string,
    previousToken?: EditorNode
  ): EditorNode[] => {
    const nodes: EditorNode[] = [];
    const curlyBracketStack: string[] = [];
    let contextString = "";
    let token = "";
    let regexOpeningChar = null;
    const delimiters = new Set(DELIMITERS);
    let index = 0;
    while (index < infix.length) {
      const char = infix[index];
      const nextChar = infix[index + 1];
      const twoCharToken = char + nextChar;

      //Section I: this section of the code is responsible for building out the regex string it allows the editor to ignore any characters that would normally be considered dlimiters, operators or context delimiters.
      if (
        STRING_CHARS.includes(char) &&
        !curlyBracketStack.length &&
        !regexOpeningChar
      ) {
        regexOpeningChar = char;
      }

      if (
        regexOpeningChar &&
        nodes[nodes.length - 1]?.type !== EditorNodeName.regex
      ) {
        if (token) {
          const node = addAttributeId(
            createEditorNode(token, getTokenType(token))
          );
          nodes.push(node);
          token = "";
        }
        const node = createEditorNode(char, EditorNodeName.regex);
        nodes.push(node);
        index++;
        continue;
      }

      if (char === regexOpeningChar) {
        nodes[nodes.length - 1].content += char;
        index++;
        regexOpeningChar = null;
        continue;
      }

      if (
        regexOpeningChar &&
        nodes[nodes.length - 1]?.type === EditorNodeName.regex
      ) {
        nodes[nodes.length - 1].content += char;
        index++;
        continue;
      }
      //end of Section I

      //Section II: This section of the code is responsible for handling the context of the attribute. It builds out the context object and attches it to the attribute node.
      if (char === CONTEXT_OPENING_BRACKET) {
        let node;
        if (token && !curlyBracketStack.length) {
          const isCustomAttribute =
            token === ATTRIBUTE_PREFIX_CHAR + CUSTOM_ATTRIBUTE_ID;
          if (isCustomAttribute) {
            node = createEditorNode(token, EditorNodeName.attribute);
            node.attributeId = CUSTOM_ATTRIBUTE_ID;
          } else {
            node = addAttributeId(createEditorNode(token, getTokenType(token)));
          }
          nodes.push(node);
          token = "";
        }
        curlyBracketStack.push(char);
      }
      if (char === CONTEXT_CLOSING_BRACKET) {
        curlyBracketStack.pop();
        if (!curlyBracketStack.length) {
          const tailNode = nodes[nodes.length - 1];
          if (tailNode.attributeId) {
            tailNode.context = JSON.parse(contextString + char);
          }
          contextString = "";
          index++;
          continue;
        }
      }
      if (curlyBracketStack.length) {
        contextString += char;
        index++;
        continue;
      }
      //End of Section II

      if (
        !delimiters.has(char) &&
        !delimiters.has(twoCharToken) &&
        char !== ATTRIBUTE_PREFIX_CHAR
      ) {
        token += char;
        index++;
        continue;
      }
      if (char === ATTRIBUTE_PREFIX_CHAR) {
        if (token) {
          const node = addAttributeId(
            createEditorNode(token, getTokenType(token))
          );
          nodes.push(node);
        }
        token = ATTRIBUTE_PREFIX_CHAR;
        index++;
        continue;
      }

      if (token) {
        const node = addAttributeId(
          createEditorNode(token, getTokenType(token))
        );
        nodes.push(addAttributeId(node));
        token = "";
      }

      if (/\S/.test(char)) {
        const prevTokenToConsider = nodes[nodes.length - 1] ?? previousToken;
        if (
          char === "-" &&
          isNumber(infix[index + 1]) &&
          infix[index - 1] !== BracketOperator.close &&
          (!prevTokenToConsider ||
            prevTokenToConsider?.type === EditorNodeName.operator ||
            prevTokenToConsider?.type === EditorNodeName.separator ||
            prevTokenToConsider?.content === BracketOperator.open ||
            prevTokenToConsider?.content === "[")
        ) {
          token += char;
          index++; // increment i here
        } else {
          const delimiter = delimiters.has(twoCharToken) ? twoCharToken : char;
          const node = addAttributeId(
            createEditorNode(delimiter, getTokenType(delimiter))
          );
          nodes.push(addAttributeId(node));
          index += delimiter.length;
        }
      } else {
        index++;
      }
    }

    if (token.trim()) {
      const type = getTokenType(token);
      const node = addAttributeId(createEditorNode(token, type));
      nodes.push(node);
    }
    return nodes;
  };

  const getPrecedence = (operatorNode: EditorNode): number => {
    return (
      functionsByNameMap.value[operatorNode?.content?.toUpperCase()]
        ?.precedence ?? 0
    );
  };

  const handleOperatorToken = (
    token: EditorNode,
    operatorsAndParenthesesStack: EditorNode[],
    attributeGroups: GroupIdentifier[],
    output: EditorNode[]
  ) => {
    if (token.id === undefined) {
      return;
    }

    const precedenceOfCurrentOperator = getPrecedence(token);

    while (
      operatorsAndParenthesesStack.length &&
      getPrecedence(operatorsAndParenthesesStack.at(-1) as EditorNode) >
        precedenceOfCurrentOperator
    ) {
      popOperatorAndPushToOutput(
        operatorsAndParenthesesStack,
        attributeGroups,
        output
      );
    }

    token.argOf = attributeGroups.at(-1)?.id;
    operatorsAndParenthesesStack.push(token);
    attributeGroups.push({ id: token.id });

    updateArgOfNodesInOutput(token, output, attributeGroups);
  };

  const updateArgOfNodesInOutput = (
    token: EditorNode,
    output: EditorNode[],
    attributeGroups: GroupIdentifier[]
  ) => {
    const rightSideOperandFirst = output.at(-1);
    const isGrouppedByParantheses = !!attributeGroups.find(
      (group) => group.id === rightSideOperandFirst?.argOf
    )?.isParanthesisGroup;

    if (isGrouppedByParantheses) {
      output.forEach((node) => {
        if (
          node.argOf === rightSideOperandFirst?.argOf &&
          node.subGroup === undefined
        ) {
          node.argOf = token.id;
        }
      });
    } else if (rightSideOperandFirst) {
      rightSideOperandFirst.argOf = token.id;
    }
  };

  const processParenthesesStack = (
    operatorsAndParenthesesStack: EditorNode[],
    attributeGroups: GroupIdentifier[],
    output: EditorNode[],
    tokens: EditorNode[],
    index: number
  ) => {
    emptyOperatorAndParenthesesStack(
      operatorsAndParenthesesStack,
      attributeGroups,
      output
    );

    operatorsAndParenthesesStack.pop();

    const currentGroupId = attributeGroups.pop()?.id;

    const lastOperator = operatorsAndParenthesesStack.at(-1);

    const groupOperator =
      tokens[index + 1]?.type === EditorNodeName.function ||
      (lastOperator &&
        (tokens[index + 1] ? getPrecedence(tokens[index + 1]) : 0) >
          getPrecedence(lastOperator))
        ? tokens[index + 1]
        : lastOperator;

    const lastGroupId = attributeGroups[attributeGroups.length - 1]?.id;

    output.forEach((node) => {
      if (node.argOf === currentGroupId) {
        if (groupOperator?.isOperator) {
          node.argOf = groupOperator.id;
        } else {
          node.argOf = lastGroupId;
        }
      }
    });
  };

  const infixToPrefix = (tokens: EditorNode[]): EditorNode[] => {
    const operatorsAndParenthesesStack: EditorNode[] = [];
    const output: EditorNode[] = [];
    const attributeGroups: GroupIdentifier[] = [];
    const reversedTokens = cloneDeep(tokens).reverse();
    reversedTokens.forEach((token, index) => {
      token.argOf = undefined;
      if (token.id === undefined) {
        token.id = uuidv4();
      }

      if (token.isOperand ?? !token.isOperator) {
        handleOperandToken(token, attributeGroups, output);
      } else if (token.isOperator) {
        handleOperatorToken(
          token,
          operatorsAndParenthesesStack,
          attributeGroups,
          output
        );
      } else if (CLOSING_PARENTHESIS.includes(token.content)) {
        handleClosingParenthesisToken(
          token,
          operatorsAndParenthesesStack,
          attributeGroups
        );
      } else if (OPENING_PARENTHESIS.includes(token.content)) {
        processParenthesesStack(
          operatorsAndParenthesesStack,
          attributeGroups,
          output,
          reversedTokens,
          index
        );
      } else if (token.type === EditorNodeName.separator) {
        handleSeparatorToken(
          operatorsAndParenthesesStack,
          attributeGroups,
          output,
          token
        );
      }
    });

    while (operatorsAndParenthesesStack.length) {
      const operator = operatorsAndParenthesesStack?.pop();
      if (operator && !PARENTHESIS.includes(operator?.content)) {
        const topAttributeGroupId = attributeGroups.at(-1)?.id;
        const currentGroupId = attributeGroups.pop();
        output.forEach((node) => {
          if (node.argOf === currentGroupId) {
            if (operator?.isOperator) {
              node.argOf = operator.id;
            } else if (topAttributeGroupId) {
              node.argOf = topAttributeGroupId;
            }
          }
        });
        output.push(operator);
      }
    }
    return output;
  };

  const createJsonNode = (
    token: EditorNode,
    args = []
  ): CustomAttributeExpression | undefined => {
    if (
      [
        EditorNodeName.number,
        EditorNodeName.unrecognized,
        EditorNodeName.regex,
      ].includes(token.type)
    ) {
      return token.type === EditorNodeName.number
        ? Number(token.content)
        : token.content;
    }
    if (token.type === EditorNodeName.attribute && token.attributeId) {
      return {
        id: token.attributeId,
        context: token.context as IContextSetValueModel,
        reference: token.id,
      };
    }
    if (token.isOperator) {
      return {
        function: token.content,
        arguments: args,
      };
    }
  };

  const convertToJson = (
    tokens: EditorNode[]
  ): CustomAttributeExpression | undefined => {
    if (!tokens.length) {
      return undefined;
    }

    if (
      tokens.length === 1 &&
      [
        EditorNodeName.number,
        EditorNodeName.attribute,
        EditorNodeName.unrecognized,
        EditorNodeName.regex,
      ].includes(tokens[0].type)
    ) {
      return createJsonNode(tokens[0]);
    }

    const argsMap = tokens.reduce(
      (acc: Record<string, CustomAttributeExpression[]>, token) => {
        let arg: CustomAttributeExpression;
        if (token.isOperator) {
          arg = {
            function: token.content.toUpperCase(),
            arguments: acc[token.id ?? ""] ?? [],
          };
        } else if (
          token.type === EditorNodeName.attribute &&
          token.attributeId
        ) {
          arg = {
            id: token.attributeId,
            context: token.context as IContextSetValueModel,
            reference: token.id,
          };
        } else {
          arg =
            token.type === EditorNodeName.number
              ? Number(token.content)
              : token.content;
        }
        if (!token.argOf) {
          return acc;
        }
        if (!acc[token.argOf]) {
          acc[token.argOf] = [];
        }
        acc[token.argOf].unshift(arg);
        return acc;
      },
      {}
    );

    const rootFunction = tokens[tokens.length - 1];
    if (rootFunction.isOperator && rootFunction.id) {
      return {
        function: rootFunction.content.toUpperCase(),
        arguments: argsMap[rootFunction.id] ?? [],
      };
    }
    return undefined;
  };
  return {
    tokenize,
    createEditorNode,
    infixToPrefix,
    convertToJson,
    functionsByNameMap,
  };
};
